/**
 * `<DessinWeb>` — un dessin du registre des fiches, peint en SVG du DOM
 * (chantier « manuel vivant », Φ-MV-2, MV-SAV-02, MV-SAV-04).
 *
 * Il ne décide de rien : `resoudreDessin` (`@silex/shared`) a déjà transformé
 * les jetons, l'escalier, l'axe en traces élémentaires — la MÊME liste que
 * celle que l'application peint en `react-native-svg` (`DessinVue.tsx`). C'est
 * ce qui garantit que le relecteur et l'élève regardent le même dessin.
 *
 * ─── La description est VISIBLE, ici ────────────────────────────────────────
 *
 * Dans l'application, la description d'un dessin (`decrit`, obligatoire dans le
 * format) n'est lue que par un lecteur d'écran. Dans la console, elle est aussi
 * écrite SOUS le dessin (`decritVisible`) : c'est le seul champ d'une fiche
 * qu'aucun contrôle automatique ne peut juger (`FORMAT-DES-FICHES.md` § 7.4,
 * point 5). Le relecteur doit pouvoir la lire en regard de ce qu'elle décrit.
 *
 * Aucune couleur littérale : chaque trace porte la classe de son RÔLE
 * (`fiche-surface--teinte`, `fiche-trait--main`), résolue par `theme.css`.
 */
import {
  direDiagramme,
  resoudreDessin,
  resoudreDiagramme,
  resoudreSolide,
  type AncrageTexte,
  type DessinFiche,
  type DessinResolu,
  type Diagramme,
  type SolideDecrit,
  type FigureV2Fiche,
  type IllustrationFiche,
  type TraceDessin,
} from '@silex/shared';
import { useMemo, type ReactNode } from 'react';

import { svgDeFigureDeFiche } from '../../lib/figures-v2-web';
import { FigureV2Web } from '../FigureV2Web';

import { DessinsFormule } from './FormuleWeb';
import { TableWeb } from './TableWeb';

const ANCRE_SVG: Readonly<Record<AncrageTexte, 'start' | 'middle' | 'end'>> = {
  debut: 'start',
  milieu: 'middle',
  fin: 'end',
};

function Trace({ t }: { readonly t: TraceDessin }): ReactNode {
  switch (t.type) {
    case 'rect':
      return (
        <rect
          x={t.x}
          y={t.y}
          width={t.largeur}
          height={t.hauteur}
          rx={t.rayon}
          className={`fiche-surface fiche-surface--${t.surface}`}
        />
      );
    case 'polygone':
      return (
        <polygon
          points={t.points.map((pt) => `${pt[0]},${pt[1]}`).join(' ')}
          className={`fiche-surface fiche-surface--${t.surface}`}
        />
      );
    case 'cercle':
      return <circle cx={t.cx} cy={t.cy} r={t.r} className={`fiche-point fiche-point--${t.ton}`} />;
    case 'chemin':
      return <path d={t.d} className={`fiche-trait fiche-trait--${t.trait}`} />;
    case 'texte':
      return (
        <text
          x={t.x}
          y={t.y}
          fontSize={t.taille}
          textAnchor={ANCRE_SVG[t.ancre]}
          className={`fiche-ecrit fiche-ecrit--${t.police} ${
            t.police === 'main' ? 'fiche-encre--main' : `fiche-encre--${t.ton}`
          }`}
        >
          {t.texte}
        </text>
      );
    case 'formule':
      return <DessinsFormule boite={t.boite} taille={t.taille} dx={t.x} dy={t.y} ton={t.ton} />;
  }
}

export interface DessinWebProps {
  /** Un dessin du registre, résolu ici. */
  readonly dessin?: DessinFiche;
  /** Un dessin DÉJÀ résolu — un diagramme statistique (`CAP-08`). L'un ou l'autre. */
  readonly resolu?: DessinResolu;
  /** Le témoin de la figure. Défaut : `fiche-dessin`. */
  readonly testid?: string;
  /** Largeur maximale à l'écran, en pixels. Sans elle : celle du conteneur. */
  readonly largeurMax?: number;
  /** Écrit la description sous le dessin, pour le relecteur. */
  readonly decritVisible?: boolean;
}

const DESSIN_VIDE: DessinResolu = { largeur: 1, hauteur: 1, decrit: '', traces: [], griefs: [] };

export function DessinWeb({
  dessin,
  resolu: dejaResolu,
  largeurMax,
  decritVisible = false,
  testid = 'fiche-dessin',
}: DessinWebProps): ReactNode {
  const resolu = useMemo(
    () => dejaResolu ?? (dessin === undefined ? DESSIN_VIDE : resoudreDessin(dessin)),
    [dessin, dejaResolu],
  );
  return (
    <figure className="fiche-dessin" data-testid={testid}>
      <svg
        className="fiche-dessin__toile"
        viewBox={`0 0 ${resolu.largeur} ${resolu.hauteur}`}
        style={{ maxWidth: Math.min(resolu.largeur, largeurMax ?? resolu.largeur) }}
        role="img"
        aria-label={resolu.decrit}
      >
        {resolu.traces.map((t, i) => (
          <Trace key={i} t={t} />
        ))}
      </svg>
      {decritVisible ? (
        <figcaption className="fiche-dessin__decrit">
          <span className="fiche-dessin__decrit-titre">
            Description lue à l’élève qui ne voit pas
          </span>
          {resolu.decrit}
        </figcaption>
      ) : null}
      {/* Un dessin que le registre n'a pas su résoudre en entier se DIT : le
          relecteur ne doit pas prendre un dessin troué pour un dessin voulu. */}
      {resolu.griefs.length > 0 ? (
        <p className="fiche-dessin__griefs" role="alert">
          Dessin incomplet : {resolu.griefs.join(' ; ')}
        </p>
      ) : null}
    </figure>
  );
}

/**
 * `<DiagrammeWeb>` — un DIAGRAMME STATISTIQUE (`CAP-08`), dans la console : ses
 * données, résolues en traces par le moteur partagé (`resoudreDiagramme`) et
 * peintes par la plume des dessins — ce que le relecteur et le tuteur voient
 * est ce que l'élève voit. Sans `decrit` (un diagramme construit par un
 * élève), ses données sont dites.
 */
export function DiagrammeWeb({
  diagramme,
  decrit,
  decritVisible = false,
  largeurMax = 560,
  testid = 'diagramme',
}: {
  readonly diagramme: Diagramme;
  readonly decrit?: string;
  readonly decritVisible?: boolean;
  readonly largeurMax?: number;
  readonly testid?: string;
}): ReactNode {
  const resolu = useMemo(
    () => resoudreDiagramme(diagramme, decrit ?? direDiagramme(diagramme)),
    [diagramme, decrit],
  );
  return (
    <DessinWeb
      resolu={resolu}
      largeurMax={largeurMax}
      decritVisible={decritVisible && decrit !== undefined}
      testid={testid}
    />
  );
}

/**
 * `<SolideWeb>` — un SOLIDE (`CAP-11`), dans la console : en perspective, en
 * développement, ou les deux, tel que l'élève le voit. `fautifPermis` : la
 * figure d'une option fausse peut montrer un patron qui ne se referme pas.
 */
export function SolideWeb({
  solide,
  fautifPermis = false,
  decritVisible = false,
  largeurMax = 560,
  testid = 'solide',
}: {
  readonly solide: SolideDecrit;
  readonly fautifPermis?: boolean;
  readonly decritVisible?: boolean;
  readonly largeurMax?: number;
  readonly testid?: string;
}): ReactNode {
  const resolu = useMemo(
    () => resoudreSolide(solide, { patronFautifPermis: fautifPermis }),
    [solide, fautifPermis],
  );
  return (
    <DessinWeb
      resolu={resolu}
      largeurMax={largeurMax}
      decritVisible={decritVisible}
      testid={testid}
    />
  );
}

/** Une illustration de fiche : un dessin du registre, ou une figure v2. */
export function IllustrationWeb({
  illustration,
  largeurMax,
  decritVisible,
}: {
  readonly illustration: IllustrationFiche;
  readonly largeurMax?: number;
  readonly decritVisible?: boolean;
}): ReactNode {
  if (illustration.genre === 'dessin') {
    return (
      <DessinWeb dessin={illustration} largeurMax={largeurMax} decritVisible={decritVisible} />
    );
  }
  /* Une figure v2 (repère, droite, triangle coté, solide) : dessinée par le
     moteur des figures de l'application (`figureVersSvg`, tranche T2) — le
     relecteur voit ce que l'élève voit. Une figure que le moteur refuse se
     DIT, avec sa description : jamais un dessin à moitié. */
  /* Un solide (`CAP-11`) : ses dimensions, que le moteur dessine. */
  if (illustration.genre === 'solide') {
    return (
      <SolideWeb
        solide={illustration}
        decritVisible={decritVisible}
        largeurMax={largeurMax}
        testid="fiche-solide"
      />
    );
  }
  /* Un diagramme statistique (`CAP-08`) : des données, que le moteur dessine. */
  if (illustration.genre === 'diagramme') {
    return (
      <DiagrammeWeb
        diagramme={illustration.diagramme}
        decrit={illustration.decrit}
        decritVisible={decritVisible}
        largeurMax={largeurMax}
        testid="fiche-diagramme"
      />
    );
  }
  /* Une table de valeurs (`CAP-09`) : ni dessin ni figure — un tableau. */
  if (illustration.genre === 'table') {
    return (
      <TableWeb
        table={illustration}
        decrit={illustration.decrit}
        decritVisible={decritVisible}
        testid="fiche-table"
      />
    );
  }
  if (illustration.annotations === undefined) {
    return (
      <FigureV2Web
        figure={illustration.figure}
        decrit={illustration.decrit}
        decritVisible={decritVisible}
        testid="fiche-figure-v2"
      />
    );
  }
  return <FigureAnnoteeWeb illustration={illustration} decritVisible={decritVisible} />;
}

/**
 * Une figure v2 ANNOTÉE (`CAP-06`) : la figure du moteur, et par-dessus, dans
 * le même cadre, ce que le prof y écrit — peint par la plume des dessins
 * (`Trace`), comme dans l'application. Une annotation que le résolveur n'a pas
 * su poser se DIT, pour le relecteur.
 */
function FigureAnnoteeWeb({
  illustration,
  decritVisible = false,
}: {
  readonly illustration: FigureV2Fiche;
  readonly decritVisible?: boolean;
}): ReactNode {
  const dessin = useMemo(() => svgDeFigureDeFiche(illustration), [illustration]);
  if (!('svg' in dessin)) {
    return (
      <FigureV2Web
        figure={illustration.figure}
        decrit={illustration.decrit}
        decritVisible={decritVisible}
        testid="fiche-figure-v2"
      />
    );
  }
  const c = dessin.cadre;
  return (
    <figure className="fiche-dessin figure-v2" data-testid="fiche-figure-v2">
      <div className="figure-v2__toile">
        <div className="figure-v2__pile">
          <div dangerouslySetInnerHTML={{ __html: dessin.svg }} />
          <svg
            className="figure-v2__surcouche"
            viewBox={`${c.x} ${c.y} ${c.largeur} ${c.hauteur}`}
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
            data-testid="fiche-figure-annotations"
          >
            {dessin.traces.map((t, i) => (
              <Trace key={i} t={t} />
            ))}
          </svg>
        </div>
      </div>
      {decritVisible ? (
        <figcaption className="fiche-dessin__decrit">
          <span className="fiche-dessin__decrit-titre">
            Description lue à l’élève qui ne voit pas
          </span>
          {illustration.decrit}
        </figcaption>
      ) : null}
      {dessin.griefs.length > 0 ? (
        <p className="fiche-dessin__griefs" role="alert">
          Annotations incomplètes : {dessin.griefs.join(' ; ')}
        </p>
      ) : null}
    </figure>
  );
}
